import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';

import { Box, Text } from '@opendoor/bricks-next';
import type { GestureResponderEvent } from 'react-native';

import { GoogleAuthRedirectButton } from '../../google/GoogleAuthRedirectButton';
import AccountEmailForm from './AccountEmailForm';
import AccountFormSubHeader from './AccountFormSubHeader';
import AccountPasswordForm from './AccountPasswordForm';
import { CheckEmailForm } from './CheckEmailForm';
import { ErrorPrompt } from './ErrorPrompt';
import { NamelessAccountContext } from './NamelessAccountContext';
import PasswordlessResultPage from './PasswordlessResultPage';
import {
  AccountFormHeader,
  AccountFormWrapper,
  type DisclaimerProps,
  EmailDisclaimerText,
  FormWrapper,
} from './shared';

// Simple Divider component to replace legacy Bricks Divider
const Divider: React.FC = () => (
  <Box height={1} backgroundColor="$borderSecondary" width="100%" my="$4x" />
);

export interface IAccountFormProps {
  /**
   * Name of the account form in analytics events, should be unique within the app
   * Convention: {appName}-{accountFormName}
   */
  analyticsName: string;
  email?: string;
  name?: string;
  phone?: string;
  loginHeaderText?: string;
  registrationHeaderText?: string;
  loginSubHeaderText?: string;
  registrationSubHeaderText?: string;
  emailLoading?: boolean;
  passwordLoading?: boolean;
  /**
   * Please make sure to pass the login error string from the backend as is we have handling for locked accounts
   */
  error?: string;
  hasGeneratedPassword?: boolean;
  isNewHuman?: boolean;
  onLogin: (email: string, password: string) => void;
  onRegister: (email: string, name: string, password: string, phone: string) => void;
  onHumanStatusRequest?: (email: string, fullName?: string) => void;
  onHumanStatusRequestOnePageReg?: (email: string) => boolean;
  /** @deprecated Ignored — phone is required when visible, optional when hidden. Derived from phoneNumberInputHidden. */
  phoneNumberInputOptional?: boolean;
  phoneNumberInputHidden?: boolean;
  openForgotPasswordInNewTab?: boolean;
  showGoogleAuth?: boolean;
  // TO DO: Refactor so we don't need to do this
  overrideHasSubmittedEmailCheckForLogin?: boolean;
  emailMismatch?: boolean;
  requestResetPassword?: (email: string) => void;
  hidePasswordField?: boolean; // When this is set to true, we hide the account password form
  onPasswordlessLoginRequest?: (
    email: string,
  ) => Promise<{ success: boolean; errors?: Array<string> }>;
  onboardingAccountPageCTA?: boolean;
  fullNamePlaceholder?: string;
  fullNameLabel?: string;
  disclaimerProps?: DisclaimerProps;
  passwordFormActionText?: string;
  postEmailCtaSection?: React.ReactNode;
  // override the width to 100%
  unsetContainer?: boolean;
  fullNameOptional?: boolean;
  fullNameHidden?: boolean;
  loginAnalyticsPrefix: string;
  /** Post-login destination forwarded to the baked-in Google auth redirect button. */
  loginRedirectTo?: string;
}

const AccountForm: React.FC<IAccountFormProps> = ({
  analyticsName,
  phone = '',
  email: initialEmail = '',
  name: initialName = '',
  loginHeaderText,
  registrationHeaderText,
  loginSubHeaderText,
  registrationSubHeaderText,
  emailLoading,
  passwordLoading,
  error,
  phoneNumberInputOptional,
  phoneNumberInputHidden,
  openForgotPasswordInNewTab,
  showGoogleAuth = false,
  overrideHasSubmittedEmailCheckForLogin,
  emailMismatch,
  requestResetPassword,
  hidePasswordField = false,
  onPasswordlessLoginRequest,
  onboardingAccountPageCTA,
  fullNamePlaceholder,
  fullNameLabel,
  disclaimerProps,
  passwordFormActionText,
  onHumanStatusRequest,
  onLogin,
  onRegister,
  postEmailCtaSection,
  unsetContainer,
  fullNameOptional,
  fullNameHidden,
  loginAnalyticsPrefix: googleAuthAnalyticsPrefix,
  loginRedirectTo,
  ...props
}) => {
  const [email, setEmail] = useState<string>(initialEmail);

  const emailInputRef = useRef(null);
  const passwordInputRef = useRef(null);

  useEffect(() => {
    if (emailInputRef.current) {
      const emailInput = emailInputRef.current as HTMLInputElement;
      emailInput.focus();
    }
  }, []);

  const [name, setName] = useState<string>(initialName);

  //Login States

  const [isNewHuman, setIsNewHuman] = useState<boolean>(!!props.isNewHuman);

  const [hasGeneratedPassword, setHasGeneratedPassword] = useState<boolean>(
    !!props.hasGeneratedPassword,
  );

  const [googleLoginAttempt, setGoogleLoginAttempt] = useState<boolean>(false);

  const [isRequestingPasswordlessLogin, setIsRequestingPasswordlessLogin] =
    useState<boolean>(false);

  const [hasPasswordlessLoginRequestSucceeded, setHasPasswordlessLogingRequestSucceeded] =
    useState<boolean>();

  const { shouldRegisterOnEmailSubmit, emailRegistrationError, onAccountUpdate } =
    useContext(NamelessAccountContext);

  const handleLogin = (password: string) => {
    setGoogleLoginAttempt(false);
    onLogin(email, password);
  };

  const handleRegister = (password: string, name: string, phone: string) => {
    setGoogleLoginAttempt(false);
    onRegister(email, name, password, phone);
  };

  const handleEmailSubmit = useCallback(
    async (email: string, name?: string) => {
      if (onHumanStatusRequest) {
        await onHumanStatusRequest(email, name);
      }
      setEmail(email);
      setName(name ?? '');
    },
    [onHumanStatusRequest],
  );

  const handleRequestPasswordlessLogin = async () => {
    if (onPasswordlessLoginRequest) {
      setIsRequestingPasswordlessLogin(true);

      const { success } = await onPasswordlessLoginRequest(email);
      setIsRequestingPasswordlessLogin(false);
      setHasPasswordlessLogingRequestSucceeded(!!success);
    }
  };

  // Toggles to the email request view and allows user to change the email being used to log in
  const resetValidEmail = (e?: React.SyntheticEvent | GestureResponderEvent) => {
    e?.preventDefault();
    setEmail('');
    setHasGeneratedPassword(false);
    setIsNewHuman(false);
  };

  const socialLoginErrorMessage = (provider: string, emailMismatch: boolean, email: string) => {
    if (emailMismatch && email && email !== '') {
      return `You tried to sign in with an email that isn’t associated with this home address. Please sign in again with ${email}.`;
    }

    return `${provider} authorization failed. Please try again or use the general login.`;
  };

  useEffect(() => {
    setEmail(initialEmail);
    /**
     * If an email was passed check its status so that we can know whether to show
     * the log in or sign up page
     */
    if (initialEmail) {
      handleEmailSubmit(initialEmail);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); //Making sure to only call on onmount with the initial Email

  useEffect(() => {
    setIsNewHuman(!!props.isNewHuman);
    setHasGeneratedPassword(!!props.hasGeneratedPassword);
  }, [props.hasGeneratedPassword, props.isNewHuman]);

  const updatingIsNewHuman = useRef(false);
  useEffect(
    () => {
      if (props.hasGeneratedPassword !== hasGeneratedPassword) {
        setHasGeneratedPassword(!!props.hasGeneratedPassword);
      }
      if (props.isNewHuman !== isNewHuman) {
        setIsNewHuman(!!props.isNewHuman);
        updatingIsNewHuman.current = true;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [initialEmail, props.hasGeneratedPassword, props.isNewHuman],
  );

  const hasEmailRegisterError = shouldRegisterOnEmailSubmit && !!emailRegistrationError;

  const hasSubmittedEmail = !!(
    email &&
    email.length > 0 &&
    !emailLoading &&
    !hasEmailRegisterError
  );

  // This logic allows skipping the password page in cases where an onAccountUpdate method is configured
  // using the NamelessAccountContext, all password page fields are hidden, and the account is for a new
  // human. Due to the way the isNewHuman state is maintained, an additional check must be performed to
  // make sure we don't invoke the callback before the state in this component is updated after the email
  // is entered and onHumanStatusRequest is invoked to actually update the isNewHuman state via a prop.
  // Note that we are not using onRegister because onRegister requires a password, and thus we wouldn't
  // be skipping the password page.
  const skipPasswordPage =
    isNewHuman &&
    hidePasswordField &&
    fullNameHidden &&
    phoneNumberInputHidden &&
    shouldRegisterOnEmailSubmit &&
    !!onAccountUpdate;

  useEffect(() => {
    if (updatingIsNewHuman.current) {
      updatingIsNewHuman.current = false;
      return;
    }

    if (hasSubmittedEmail && skipPasswordPage) {
      onAccountUpdate(name, phone, false);
    }
  }, [hasSubmittedEmail, name, onAccountUpdate, phone, skipPasswordPage]);

  if (hasSubmittedEmail && hasGeneratedPassword) {
    return (
      <AccountFormWrapper maxWidth={unsetContainer ? '100%' : 480}>
        <CheckEmailForm
          analyticsName={analyticsName}
          email={email}
          hasGeneratedPassword={hasGeneratedPassword}
          onResetValidEmail={resetValidEmail}
          requestResetPassword={requestResetPassword}
        />
      </AccountFormWrapper>
    );
  }

  if (hasPasswordlessLoginRequestSucceeded !== undefined) {
    return (
      <PasswordlessResultPage
        hasSucceeded={hasPasswordlessLoginRequestSucceeded}
        email={email}
        analyticsName={analyticsName}
      />
    );
  }

  // TODO: Clean up error handling
  const hasGoogleError = googleLoginAttempt && (emailMismatch || error);
  let loginErrorMessage = '';
  if (emailMismatch && email) {
    loginErrorMessage = `You are logged in with an email that isn’t associated with this home address. Please sign in again with ${email}.`;
  } else if (error && !googleLoginAttempt) {
    if (error.includes('You have one more attempt before your account is locked.')) {
      loginErrorMessage = 'You’ll be locked out after this failed attempt.';
    } else if (error.includes('Your account is locked.')) {
      loginErrorMessage =
        'You’re locked out of your account due to the number of failed password attempts. Check your email to reset your password or try again in 1 hour.';
    } else {
      loginErrorMessage = 'Invalid email or password';
    }
  }
  let headerText = loginHeaderText != null ? loginHeaderText : 'Sign in or create an account';

  if (hasSubmittedEmail && isNewHuman) {
    headerText =
      registrationHeaderText != null ? registrationHeaderText : 'Finish creating an account';
  } else if (hasSubmittedEmail) {
    headerText = 'Welcome back';
  }

  const showGoogleAuthSection =
    showGoogleAuth && (!hasSubmittedEmail || overrideHasSubmittedEmailCheckForLogin);

  return (
    <AccountFormWrapper maxWidth={unsetContainer ? '100%' : hasSubmittedEmail ? 560 : 400}>
      {/* Only show disclaimer when user enters their email */}
      {headerText && <AccountFormHeader>{headerText}</AccountFormHeader>}
      {!hasSubmittedEmail && disclaimerProps?.location !== 'bottom' && (
        <EmailDisclaimerText analyticsName={analyticsName} />
      )}
      <AccountFormSubHeader
        isNewHuman={isNewHuman}
        email={email}
        hasSubmittedEmail={hasSubmittedEmail}
        loginSubHeaderText={loginSubHeaderText}
        registrationSubHeaderText={registrationSubHeaderText}
        onValidEmailReset={resetValidEmail}
        hidePasswordField={hidePasswordField}
        phoneNumberInputHidden={phoneNumberInputHidden}
      />
      {showGoogleAuthSection && (
        <Box
          justifyContent="center"
          alignItems="center"
          marginTop={headerText || loginSubHeaderText || registrationSubHeaderText ? '$20x' : 0}
        >
          <GoogleAuthRedirectButton
            analyticsPrefix={googleAuthAnalyticsPrefix}
            redirectTo={loginRedirectTo}
          />
        </Box>
      )}
      {hasGoogleError && (
        <ErrorPrompt message={socialLoginErrorMessage('Google', !!emailMismatch, email)} />
      )}
      {showGoogleAuthSection && (
        <Box
          gap="$4x"
          marginTop="$20x"
          alignItems="center"
          justifyContent="space-between"
          flexDirection="row"
        >
          <Box flex={1}>
            <Divider />
          </Box>
          <Text typography="$bodyMedium" tag="p" color="$contentTertiary" paddingHorizontal={7}>
            or
          </Text>
          <Box flex={1}>
            <Divider />
          </Box>
        </Box>
      )}
      <FormWrapper marginTop="$20x">
        {hasSubmittedEmail && !skipPasswordPage ? (
          <AccountPasswordForm
            analyticsName={analyticsName}
            email={email}
            name={name}
            phone={phone}
            loading={passwordLoading}
            isNewHuman={isNewHuman}
            loginError={loginErrorMessage}
            onLogin={handleLogin}
            onRegister={handleRegister}
            phoneNumberInputOptional={phoneNumberInputHidden ?? false}
            phoneNumberInputHidden={phoneNumberInputHidden}
            openForgotPasswordInNewTab={openForgotPasswordInNewTab}
            showPasswordlessLogin={!isNewHuman && !!onPasswordlessLoginRequest}
            onRequestPasswordlessLogin={handleRequestPasswordlessLogin}
            isRequestingPasswordlessLogin={isRequestingPasswordlessLogin}
            hidePasswordField={hidePasswordField}
            onboardingAccountPageCTA={onboardingAccountPageCTA}
            fullNamePlaceholder={fullNamePlaceholder ? fullNamePlaceholder : ''}
            fullNameLabel={fullNameLabel ? fullNameLabel : 'Full name'}
            disclaimerProps={disclaimerProps}
            ref={passwordInputRef}
            passwordFormActionText={passwordFormActionText}
            fullNameOptional={fullNameOptional}
            fullNameHidden={fullNameHidden}
          />
        ) : (
          <Box gap="$12x">
            <AccountEmailForm
              analyticsName={analyticsName}
              email={email}
              ref={emailInputRef}
              loading={emailLoading}
              onSubmit={handleEmailSubmit}
              openForgotPasswordInNewTab={openForgotPasswordInNewTab}
              showForgotPassword={!hidePasswordField}
            />
            {disclaimerProps?.location === 'bottom' && (
              <EmailDisclaimerText analyticsName={analyticsName} />
            )}
          </Box>
        )}
      </FormWrapper>
    </AccountFormWrapper>
  );
};

export default AccountForm;
